// TODO: Implement localization i18n
import { faPen, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, TextInput, Search, Loading } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { Currency } from "interfaces/dataInterfaces"
import { useEffect, useState } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { SubmitHandler, useForm } from "react-hook-form"
import { GET_ITEM_LIMIT, MARKET_MODULE } from "constants/constants"
import {
  useAddCurrencyMutation,
  useDeleteCurrencyMutation,
  useGetCurrenciesQuery,
  useUpdateCurrencyMutation,
} from "services/core/currencies"
import useCommon from "hooks/useCommon"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Inputs = {
  name: string
  extendedName: string
  exchangeRateSymbol: string
}
const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}
export default function CurrencyPage() {
  const { showSnackBar } = useCommon()

  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  const [actionItem, setActionItem] = useState<{
    item: Currency
    action: "edit" | "delete"
  } | null>(null)
  const [query, setQuery] = useState<string | undefined>("")
  const [inputText, setInputText] = useState<string | undefined>(undefined)
  const [showNewCurrencyModal, setShowNewCurrencyModal] = useState(false)

  const { data: currencies, isFetching: isLoadingCurrencies } = useGetCurrenciesQuery({
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
    query,
  })
  const [addCurrency, { isLoading: isLoadingAdd }] = useAddCurrencyMutation()
  const [updateCurrency, { isLoading: isLoadingEdit }] = useUpdateCurrencyMutation()
  const [deleteCurrency, { isLoading: isLoadingDelete }] = useDeleteCurrencyMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<Inputs>()

  const onSubmit: SubmitHandler<Inputs> = async data => {
    const {
      name: currencyName,
      extendedName: currencyNameExtended,
      exchangeRateSymbol: currencyExchangeRateSymbol,
    } = data
    if (actionItem?.action === "edit") {
      try {
        await updateCurrency({
          currencyID: actionItem.item.currencyID,
          currencyName,
          currencyNameExtended,
          currencyExchangeRateSymbol,
        }).unwrap()
        setActionItem(null)
        showSnackBar("Currency updated successfully!")
      } catch (error) {
        showSnackBar("Currency not updated", "error")
      }
    } else {
      try {
        await addCurrency({ currencyName, currencyNameExtended, currencyExchangeRateSymbol }).unwrap()
        setShowNewCurrencyModal(false)
        showSnackBar("Currency added successfully!")
      } catch (error) {
        showSnackBar("Currency not added", "error")
      }
    }
    reset()
  }

  const columns: Array<ColumnShape<Currency>> = [
    {
      key: "currencyID",
      dataKey: "currencyID",
      title: "ID",
      width: 80,
      maxWidth: 80,
    },
    {
      key: "currencyName",
      dataKey: "currencyName",
      title: "Code",
      width: 200,
    },
    {
      key: "currencyNameExtended",
      dataKey: "currencyNameExtended",
      title: "Name",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "currencyExchangeRateSymbol",
      dataKey: "currencyExchangeRateSymbol",
      title: "Exchange rate symbol",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "action",
      width: 0,
      align: Column.Alignment.CENTER,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <PermissionWrapper {...permissionsProps} feature="edit">
          <>
            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "edit",
                })
              }
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />

            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </>
        </PermissionWrapper>
      ),
    },
  ]

  const handleDelete = async (id: number) => {
    try {
      await deleteCurrency(id)
      setActionItem(null)
      showSnackBar("Currency deleted successfully!")
    } catch (error) {
      showSnackBar("Currency not deleted", "error")
    }
  }

  const handleNewEditModalClose = () => {
    reset()
    actionItem ? setActionItem(null) : setShowNewCurrencyModal(false)
  }

  const handleSearch = (e: any) => {
    setInputText(e)
  }

  const handleSubmitQuery = () => {
    setPageNumber(1)
    setQuery(inputText)
  }

  const handleSearchKey = (e: any) => {
    inputText && e?.code === "Enter" && handleSubmitQuery()
  }

  useEffect(() => {
    !inputText && query && setQuery("")
  }, [inputText])

  useEffect(() => {
    reset()
    if (actionItem?.action === "edit") {
      setValue("name", actionItem?.item.currencyName)
      setValue("extendedName", actionItem?.item?.currencyNameExtended || "")
      setValue("exchangeRateSymbol", actionItem?.item?.currencyExchangeRateSymbol || "")
    }
  }, [actionItem])

  return (
    <>
      {(isLoadingAdd || isLoadingEdit || isLoadingDelete) && <Loading />}
      <HeaderFilters>
        <div className="d-flex" style={{ width: "300px" }}>
          <Search
            id="query"
            name="query"
            placeholder="Type here to filter results..."
            className="text-input mr-2"
            value={query || ""}
            onChange={e => handleSearch(e)}
            onKeyUp={e => handleSearchKey(e)}
          />
          <Button action={handleSubmitQuery} label="Search" className="btn-full blue" icon={faSearch} />
        </div>

        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button
            action={() => setShowNewCurrencyModal(true)}
            label="Add new currency"
            className="btn-full green"
            icon={faPlus}
          />
        </PermissionWrapper>
      </HeaderFilters>

      <div
        style={{ height: "100%", minHeight: "60vh" }}
        className={isLoadingCurrencies ? "mt-2 d-flex align-center justify-center" : "mt-2"}
      >
        {isLoadingCurrencies ? (
          <Loading withOverlay={false} />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={currencies?.items}
                width={width}
                height={height}
                rowKey="currencyID"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>

      <Modal
        open={actionItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.currencyID)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      <Modal
        open={showNewCurrencyModal || actionItem?.action === "edit"}
        modalHeading={actionItem?.action === "edit" ? "Modify currency" : "Add new currency"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSubmit(onSubmit)}
        onRequestClose={handleNewEditModalClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            labelText="Name"
            placeholder="Currency name"
            defaultValue={actionItem?.item.currencyName}
            invalid={errors.name}
            invalidText={errors.name?.message}
            {...register("name", { required: "Name is required" })}
          />
          <div className="mt-3">
            <TextInput
              labelText="Extended name"
              placeholder="Extended currency name"
              defaultValue={actionItem?.item.currencyNameExtended ?? ""}
              {...register("extendedName")}
            />
          </div>
          <div className="mt-3">
            <TextInput
              labelText="Exchange Rate Symbol"
              placeholder="Exchange Rate Symbol"
              defaultValue={actionItem?.item.currencyExchangeRateSymbol ?? ""}
              {...register("exchangeRateSymbol")}
            />
          </div>
        </form>
      </Modal>

      <Pagination
        totalItems={currencies?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any): void => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
    </>
  )
}
